<script setup>
import PublicPaymentStore from '../components/PublicPaymentStore.vue'
import { ref, computed, onMounted } from 'vue'
import { api } from '@/api/client'
import { EP } from '@/api/endpoints'

import HeadlineFraunces from '@/design/primitives/HeadlineFraunces.vue'
import CoinGlyph         from '@/design/primitives/CoinGlyph.vue'
import ButtonSeed        from '@/design/primitives/ButtonSeed.vue'
import FieldLeaf         from '@/design/primitives/FieldLeaf.vue'

import IconSearch        from '@/design/icons/IconSearch.vue'

import PosterCard        from './Market/parts/PosterCard.vue'
import SkeletonPoster    from './Market/parts/SkeletonPoster.vue'

// ─── Data state ───────────────────────────────────────────────────────────────
const loading    = ref(true)
const allFailed  = ref(false)

const categories = ref([])  // [{ id, name }]
const products   = ref([])  // [{ id, name, description, price, imageUrl, categoryId, _kind }]
const arkItems   = ref([])  // [{ id, name, description, price, imageUrl, _kind }]
const creatures  = ref([])  // [{ id, name, description, price, imageUrl, _kind }]

// ─── Filter / view state ──────────────────────────────────────────────────────
const search         = ref('')
const activeTab      = ref('all')    // 'all' | 'products' | 'dinos' | 'items'
const activeCategory = ref('all')   // category id or 'all'

const TABS = [
  { key: 'all',      label: 'ALL'      },
  { key: 'products', label: 'PRODUCTS' },
  { key: 'dinos',    label: 'DINOS'    },
  { key: 'items',    label: 'ITEMS'    }
]

// ─── Normalizers ─────────────────────────────────────────────────────────────
function normalizeDict(dict, idKey, kindLabel) {
  if (!dict) return []
  if (Array.isArray(dict)) {
    return dict.map(val => ({
      id:          val[idKey] ?? val.id,
      name:        val.name ?? '',
      description: val.description ?? val.category ?? '',
      price:       Number(val.price ?? 0),
      imageUrl:    val.image_url ?? val.image ?? val.imageUrl ?? '',
      _kind:       kindLabel
    }))
  }
  return Object.entries(dict).map(([key, val]) => ({
    id:          val[idKey] ?? key,
    name:        val.name ?? '',
    description: val.description ?? val.category ?? '',
    price:       Number(val.price ?? 0),
    imageUrl:    val.image_url ?? val.image ?? val.imageUrl ?? '',
    _kind:       kindLabel
  }))
}

// ─── Data fetch ───────────────────────────────────────────────────────────────
async function fetchAll() {
  loading.value   = true
  allFailed.value = false
  let hits = 0

  // 1. Categories
  try {
    const res = await api.get(EP.categories)
    categories.value = Array.isArray(res.data) ? res.data : []
    hits++
  } catch { /* partial failure */ }

  // 2. Products
  try {
    const res = await api.get(EP.products)
    const raw = Array.isArray(res.data) ? res.data : []
    products.value = raw.map(p => ({
      id:          p.id ?? p._id,
      name:        p.name ?? '',
      description: p.description ?? '',
      price:       Number(p.price ?? p.priceGems ?? 0),
      imageUrl:    p.image ?? p.image_url ?? p.imageUrl ?? '',
      categoryId:  p.categoryId ?? p.category_id ?? null,
      // Delivery type drives the DINOS/ITEMS tab split (creatures vs items). The
      // storefront shows ONLY these priced+deliverable products — not the raw,
      // price-less, non-purchasable shop-api catalog.
      delivery:    p.arkDeliveryType ?? p.deliveryType ?? (p.category === 'Creatures' ? 'creature' : 'item'),
      category:    p.category ?? '',
      _kind:       'product'
    }))
    hits++
  } catch { /* partial failure */ }

  // (Raw shop-api /items + /creatures catalogs are intentionally NOT loaded into
  //  the storefront — they carry no price and aren't purchasable. The seeded
  //  products above ARE the sellable dinos + items, split by delivery type.)

  // 5. Creature categories (informational)
  try {
    await api.get(EP.arkCreatureCategories)
    hits++
  } catch { /* non-critical */ }

  if (hits === 0) allFailed.value = true
  loading.value = false
}

onMounted(fetchAll)

// ─── Product splits by delivery type (drive DINOS / ITEMS tabs) ──────────────
const productDinos = computed(() => products.value.filter(p => p.delivery === 'creature'))
const productItems = computed(() => products.value.filter(p => p.delivery !== 'creature'))

// ─── Tag helper — assigns a pill label per item kind ─────────────────────────
function resolveTag(item) {
  if (item.delivery === 'creature' || item._kind === 'creature') return 'DINO'
  if (item.delivery === 'item' || item._kind === 'item') return 'ITEM'
  return ''
}

// ─── Active dataset (by tab) ──────────────────────────────────────────────────
const activeDataset = computed(() => {
  switch (activeTab.value) {
    case 'products': return products.value
    case 'dinos':    return productDinos.value
    case 'items':    return productItems.value
    default:
      // ALL — every sellable product
      return products.value
  }
})

// ─── Filtered list (category + search) ───────────────────────────────────────
const filtered = computed(() => {
  let list = activeDataset.value

  // Never show price-less entries (e.g. shop-api bundles with no price) — they're
  // not purchasable and a "0" tile reads as broken.
  list = list.filter(item => Number(item.price) > 0)

  // Category filter — only when on PRODUCTS tab
  if (activeTab.value === 'products' && activeCategory.value !== 'all') {
    list = list.filter(p => String(p.categoryId) === activeCategory.value)
  }

  // Text search
  const q = search.value.trim().toLowerCase()
  if (q) {
    list = list.filter(item =>
      (item.name ?? '').toLowerCase().includes(q) ||
      (item.description ?? '').toLowerCase().includes(q)
    )
  }

  return list
})

// ─── Tab change helper ────────────────────────────────────────────────────────
function setTab(key) {
  activeTab.value      = key
  activeCategory.value = 'all'
}

// ─── Clear filters ────────────────────────────────────────────────────────────
function clearFilters() {
  search.value         = ''
  activeTab.value      = 'all'
  activeCategory.value = 'all'
}
const marketQuote=ref(null)
function fiatPrice(gems){const quote=marketQuote.value;if(!quote?.tokensPerEur)return '';const rate=Number(quote.currencies.find(c=>c.code===quote.currency)?.rate);if(!(rate>0))return '';return new Intl.NumberFormat('en',{style:'currency',currency:quote.currency}).format(Number(gems)/quote.tokensPerEur*rate)}
</script>

<template>
  <div class="armory u-fade-in">

    <!-- ═══════════════════════════════════════════════════════════════════════
         PAGE HEADER BAND — ~30vh, cinematic dark, sits over global bg
    ════════════════════════════════════════════════════════════════════════ -->
    <header class="armory__header">
      <div class="armory__header-inner">
        <p class="armory__kicker u-display u-allcaps u-gold">STORE · EVOLUXU</p>

        <HeadlineFraunces level="h1">
          The <em>Armory</em>
        </HeadlineFraunces>

        <p class="armory__subtitle">
          Gear and dinos. Forged by survivors, paid in EvoGems.
        </p>
      </div>
    </header>
    <PublicPaymentStore @quote="marketQuote=$event"/>

    <!-- ═══════════════════════════════════════════════════════════════════════
         FILTER / VIEW-TOGGLE BAND — sticky glass strip
    ════════════════════════════════════════════════════════════════════════ -->
    <div class="armory__filter-band" role="navigation" aria-label="Catalog filters">
      <div class="armory__filter-inner">

        <!-- Search — left -->
        <div class="armory__search-wrap">
          <div class="armory__search-icon" aria-hidden="true">
            <IconSearch />
          </div>
          <FieldLeaf
            v-model="search"
            placeholder="Search the catalog…"
            aria-label="Search catalog"
            class="armory__search-field"
          />
        </div>

        <!-- Tab toggles — center -->
        <nav class="armory__tabs" role="tablist" aria-label="Catalog sections">
          <button
            v-for="tab in TABS"
            :key="tab.key"
            class="armory__tab u-display u-allcaps"
            :class="{ 'armory__tab--active': activeTab === tab.key }"
            role="tab"
            :aria-selected="activeTab === tab.key"
            @click="setTab(tab.key)"
          >
            {{ tab.label }}
          </button>
        </nav>

        <!-- Category dropdown — right, only when PRODUCTS tab active -->
        <div class="armory__cat-wrap">
          <select
            v-if="activeTab === 'products'"
            v-model="activeCategory"
            class="armory__cat-select u-display"
            aria-label="Filter by category"
          >
            <option value="all">ALL CATEGORIES</option>
            <option
              v-for="cat in categories"
              :key="cat.id"
              :value="String(cat.id)"
            >
              {{ (cat.name ?? '').toUpperCase() }}
            </option>
          </select>
        </div>

      </div>
    </div>

    <!-- ═══════════════════════════════════════════════════════════════════════
         CATALOG — 3-column poster grid
    ════════════════════════════════════════════════════════════════════════ -->
    <main
      class="armory__aisle"
      aria-live="polite"
      :aria-busy="loading ? 'true' : 'false'"
    >
      <div class="armory__aisle-inner">

        <!-- Loading: 6 skeleton posters -->
        <template v-if="loading">
          <div class="armory__shelf">
            <SkeletonPoster v-for="n in 6" :key="n" />
          </div>
        </template>

        <!-- All fetches failed -->
        <template v-else-if="allFailed">
          <div class="armory__state">
            <p class="armory__state-text u-display u-allcaps">
              Catalog offline.
            </p>
            <ButtonSeed variant="ghost" size="sm" @click="fetchAll">
              RETRY
            </ButtonSeed>
          </div>
        </template>

        <!-- Empty after filters -->
        <template v-else-if="filtered.length === 0">
          <div class="armory__state">
            <p class="armory__state-text u-display u-allcaps">
              Nothing in this aisle.
            </p>
            <ButtonSeed variant="ghost" size="sm" @click="clearFilters">
              CLEAR FILTERS
            </ButtonSeed>
          </div>
        </template>

        <!-- Catalog grid -->
        <template v-else>
          <div class="armory__shelf">
            <PosterCard
              v-for="item in filtered"
              :key="`${item._kind}-${item.id}`"
              :id="item.id"
              :kind="item._kind"
              :name="item.name"
              :description="item.description"
              :price="item.price" :fiat="fiatPrice(item.price)"
              :imageUrl="item.imageUrl"
              :tag="resolveTag(item)"
            />
          </div>
        </template>

      </div>
    </main>

  </div>
</template>

<style scoped>
/* ═══════════════════════════════════════════════════════════════════════════
   PAGE SHELL — transparent; global BackgroundField shows through
═══════════════════════════════════════════════════════════════════════════ */
.armory {
  min-height: 100vh;
  padding-bottom: var(--footer-pad);
  /* No background — global panorama + gradient shows through */
}

/* ═══════════════════════════════════════════════════════════════════════════
   HEADER BAND — ~30vh, transparent dark
═══════════════════════════════════════════════════════════════════════════ */
.armory__header {
  border-bottom: 1px solid var(--ash);
  position: relative;
  min-height: 30vh;
  display: flex;
  align-items: flex-end;
  padding: var(--sp-24) var(--sp-6) var(--sp-12);
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(11, 10, 8, 0.6) 70%,
    rgba(11, 10, 8, 0.88) 100%
  );
}

/* Extra local dark veil so header text pops over the panorama */
.armory__header-inner {
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 0 var(--sp-6);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--sp-3);
}

.armory__kicker {
  margin: 0;
  font-size: var(--fs-12);
  letter-spacing: 0.22em;
}

.armory__subtitle {
  margin: 0;
  font-family: var(--font-body);
  font-size: var(--fs-16);
  color: var(--ink-soft);
  line-height: var(--lh-body);
  max-width: 52ch;
}

/* ═══════════════════════════════════════════════════════════════════════════
   FILTER BAND — sticky glass strip
═══════════════════════════════════════════════════════════════════════════ */
/* Filter band — visual 1:1 with Trading's glass-rail */
.armory__filter-band {
  position: relative;
  z-index: 1;
  background: var(--bg-glass-strong);
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
  border-top: 1px solid var(--ash);
  border-bottom: 1px solid var(--ash);
  box-shadow: var(--shadow-1);
}

.armory__filter-inner {
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: var(--sp-4) var(--sp-6);
  display: flex;
  align-items: center;
  gap: var(--sp-4);
  flex-wrap: wrap;
}

/* Search — grows to fill */
.armory__search-wrap {
  position: relative;
  flex: 1 1 240px;
}

.armory__search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  color: var(--ink-mute);
  pointer-events: none;
  z-index: 1;
  display: flex;
  align-items: center;
}

/* Match search input height to category dropdown (46px) */
.armory__search-field :deep(.field__input) {
  padding: 0 18px 0 40px;
  height: 46px;
}

/* Tabs */
.armory__tabs {
  display: flex;
  align-items: center;
  gap: 0;
  flex: 0 0 auto;
}

.armory__tab {
  padding: 0 var(--sp-4);
  height: 46px;
  display: inline-flex;
  align-items: center;
  font-family: var(--font-display);
  font-size: var(--fs-12);
  letter-spacing: 0.10em;
  text-transform: uppercase;
  color: var(--ink-soft);
  background: transparent;
  border: none;
  cursor: pointer;
  transition: color var(--t-fast) var(--ease-road);
}
.armory__tab:hover { color: var(--ink); }
.armory__tab--active { color: var(--gold); }

/* Category dropdown — same shape as Trading's sort */
.armory__cat-wrap {
  position: relative;
  flex: 0 0 auto;
}
.armory__cat-wrap::after {
  content: "";
  position: absolute;
  right: 14px;
  top: 50%;
  transform: translateY(-50%);
  width: 0;
  height: 0;
  border-left: 4px solid transparent;
  border-right: 4px solid transparent;
  border-top: 5px solid var(--ink-soft);
  pointer-events: none;
}
.armory__cat-select {
  appearance: none;
  -webkit-appearance: none;
  padding: 10px 36px 10px 14px;
  height: 46px;
  border: 1px solid var(--ash-strong);
  border-radius: var(--r-xs);
  background: transparent;
  color: var(--ink);
  font-family: var(--font-display);
  font-size: var(--fs-12);
  font-weight: 400;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  cursor: pointer;
  transition: border-color var(--t-fast) var(--ease-road),
              background var(--t-fast) var(--ease-road);
}
.armory__cat-select:hover { background: var(--bg-deep); border-color: var(--gold); }
.armory__cat-select:focus {
  outline: none;
  border-color: var(--gold);
  box-shadow: inset 0 -2px 0 var(--gold);
}
.armory__cat-select option { background: var(--bg-deep); color: var(--ink); }

/* ═══════════════════════════════════════════════════════════════════════════
   AISLE — main content area
═══════════════════════════════════════════════════════════════════════════ */
.armory__aisle {
  padding: var(--sp-8) 0 0;
}

.armory__aisle-inner {
  max-width: var(--container-wide);
  margin: 0 auto;
  padding: 0 var(--sp-6);
}

/* ── Shelf: 3-col responsive grid ── */
.armory__shelf {
  display: grid;
  grid-template-columns: 1fr;
  gap: var(--sp-6);
}

@media (min-width: 860px) {
  .armory__shelf {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1280px) {
  .armory__shelf {
    grid-template-columns: repeat(3, 1fr);
  }
}

/* ── Empty / error state — visual 1:1 with Trading's .bazaar-page__state ── */
.armory__state {
  padding: var(--sp-12) var(--sp-6);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--sp-4);
  text-align: center;
}
.armory__state-text {
  font-size: var(--fs-16);
  color: var(--ink-soft);
  margin: 0;
  letter-spacing: 0.08em;
}

/* ═══════════════════════════════════════════════════════════════════════════
   RESPONSIVE ADJUSTMENTS
═══════════════════════════════════════════════════════════════════════════ */
@media (max-width: 860px) {
  .armory__filter-inner {
    flex-wrap: wrap;
    gap: var(--sp-3);
    padding: var(--sp-2) var(--sp-4);
  }

  .armory__search-wrap {
    width: 100%;
    order: 1;
  }

  .armory__tabs {
    order: 2;
    width: 100%;
    justify-content: flex-start;
    overflow-x: auto;
    scrollbar-width: none;
  }

  .armory__tabs::-webkit-scrollbar { display: none; }

  .armory__cat-wrap {
    order: 3;
    width: 100%;
    justify-content: flex-start;
  }

  .armory__cat-select {
    width: 100%;
  }

  .armory__aisle-inner {
    padding: 0 var(--sp-4);
  }
}
</style>
